/**
 * Atribuição de origem (UTM / Google Ads) para os contatos de WhatsApp.
 *
 * IMPORTANTE — por que não basta "colocar UTM no link do WhatsApp":
 * o `wa.me` só aceita o parâmetro `text` (a mensagem). Qualquer `utm_*` colado
 * na URL do WhatsApp é descartado e não chega a lugar nenhum. A UTM serve para
 * o caminho ANÚNCIO → LANDING PAGE.
 *
 * Portanto, o que fazemos aqui: lemos a UTM/gclid na chegada da landing page,
 * guardamos na sessão e CARIMBAMOS a origem dentro do texto da mensagem — assim
 * o atendente enxerga na própria conversa que o lead veio do Google Ads, de qual
 * campanha e de qual página. Os mesmos dados também vão para o dataLayer, para
 * segmentar no GA4.
 *
 * Visitante orgânico não gera carimbo nenhum (sufixo vazio).
 */

const STORAGE_KEY = 'pacces_attr';

const TRACKED_PARAMS = [
    'utm_source',
    'utm_medium',
    'utm_campaign',
    'utm_term',
    'utm_content',
    'gclid',
    'wbraid',
    'gbraid',
    'fbclid',
] as const;

export type Attribution = Partial<Record<(typeof TRACKED_PARAMS)[number], string>>;

const readFromUrl = (): Attribution | null => {
    if (typeof window === 'undefined') return null;

    const params = new URLSearchParams(window.location.search);
    const found: Attribution = {};

    TRACKED_PARAMS.forEach((key) => {
        const value = params.get(key);
        if (value) found[key] = value;
    });

    return Object.keys(found).length > 0 ? found : null;
};

const readFromStorage = (): Attribution => {
    if (typeof window === 'undefined') return {};
    try {
        const raw = window.sessionStorage.getItem(STORAGE_KEY);
        return raw ? (JSON.parse(raw) as Attribution) : {};
    } catch {
        return {};
    }
};

/**
 * Atribuição da sessão. Se a URL atual traz parâmetros, eles são gravados
 * (a chegada mais recente vence); caso contrário, reaproveita o que já foi
 * capturado na sessão — o visitante pode navegar antes de clicar no WhatsApp.
 */
export const getAttribution = (): Attribution => {
    const fromUrl = readFromUrl();

    if (fromUrl) {
        try {
            window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(fromUrl));
        } catch {
            /* sessionStorage indisponível (modo privado): segue sem persistir */
        }
        return fromUrl;
    }

    return readFromStorage();
};

/** Rótulo legível da origem. `null` quando não há sinal de campanha. */
export const getOriginLabel = (attr: Attribution = getAttribution()): string | null => {
    const isGoogleAds =
        !!attr.gclid || !!attr.gbraid || !!attr.wbraid || attr.utm_source === 'google';

    if (isGoogleAds) return 'Google Ads';
    if (attr.fbclid || attr.utm_source === 'facebook' || attr.utm_source === 'instagram') {
        return 'Meta Ads';
    }
    if (attr.utm_source) {
        return attr.utm_medium ? `${attr.utm_source} / ${attr.utm_medium}` : attr.utm_source;
    }
    return null;
};

/**
 * ⚠️ NÃO USAR NA MENSAGEM DO CLIENTE — mantido apenas para referência.
 *
 * Este bloco já foi anexado ao texto do WhatsApp e é um forte suspeito de ter
 * matado conversas: o cliente abria o app e via, na mensagem que ELE ia enviar
 * sobre si mesmo, cinco linhas declarando que veio de um anúncio, de qual
 * campanha e o que havia pesquisado. Parece vigilância e cheira a spam. No
 * período em que esteve ativo: 2 cliques no botão, 0 mensagens enviadas.
 *
 * A origem agora viaja no evento `conversion_click` (ver analytics.ts), que é
 * onde rastreamento deve viver. A mensagem leva só o texto do cliente.
 */
export const getWhatsAppAttributionSuffix = (): string => {
    const attr = getAttribution();
    const origin = getOriginLabel(attr);

    if (!origin) return '';

    const lines = ['', '---', `_Origem: ${origin}_`];

    if (attr.utm_campaign) lines.push(`_Campanha: ${attr.utm_campaign}_`);
    if (attr.utm_term) lines.push(`_Termo: ${attr.utm_term}_`);
    if (typeof window !== 'undefined') lines.push(`_Página: ${window.location.pathname}_`);

    return lines.join('\n');
};

/** Campos de atribuição para enviar junto dos eventos do dataLayer (GA4). */
export const getAttributionParams = (): Record<string, string> => {
    const attr = getAttribution();
    const params: Record<string, string> = {};

    if (attr.utm_source) params.utm_source = attr.utm_source;
    if (attr.utm_medium) params.utm_medium = attr.utm_medium;
    if (attr.utm_campaign) params.utm_campaign = attr.utm_campaign;
    if (attr.utm_term) params.utm_term = attr.utm_term;
    if (attr.utm_content) params.utm_content = attr.utm_content;
    if (attr.gclid) params.gclid = attr.gclid;

    const origin = getOriginLabel(attr);
    if (origin) params.lead_origin = origin;

    return params;
};
